import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ContactCardRow, ContactGridRow, ContactRow } from './ContactRows';
import { useListFetch } from './useListFetch';
import { ListStateShell } from './ListStateShell';

/**
 * ContactsGrid — #62 Phase 2 (UXeng's per-screen spec §2). Same one-item-list responsive tier-switch
 * as LeadsGrid (card <1024, table >=1024 — a single rendered list, no CSS-hidden duplicate testids),
 * but a DIFFERENT column set: no editable pills (Contacts carries no per-tenant triage attributes yet),
 * Account/Title/Email/Last Contacted instead of clientStatus/qualification. Fetches
 * `/api/customers?kind=contact` — the SAME rolodex endpoint the Leads screen uses, filtered to the
 * OTHER kind (D3 shape (a): Leads and Contacts are `kind` filters on one table, never two data sources).
 */
export const ContactsGrid: React.FC<{
  selectedId?: string | null;
  onOpenPerson: (contact: ContactRow) => void;
  /** Bumped by the parent after a create succeeds (#62 Contacts depth spec) — this component owns its
   *  own fetch (separate from the parent's `customers` state), so it needs an explicit re-fetch signal
   *  rather than relying on a prop change it has no other way to observe. */
  refreshKey?: number;
}> = ({ selectedId, onOpenPerson, refreshKey }) => {
  const { t } = useTranslation('common');

  const [isDesktop, setIsDesktop] = useState(typeof window !== 'undefined' && window.innerWidth >= 1024);

  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= 1024);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // X-1 via useListFetch (P38 surface 4 of 10).
  const list = useListFetch<ContactRow[]>('/api/customers?kind=contact', {
    refreshKey,
    map: (rows) => (rows as any[]).map((l) => ({
      id: l.id, leadId: l.leadId ?? l.id,
      // B5/D-L5 — a token-named CONTACT renders "Unnamed contact" with the token demoted to
      // muted secondary text: a hex string in the Name slot claims an identity nobody has. The
      // LEADS grid deliberately keeps its hex fallback (token-keyed leads stay findable there).
      name: l.name || l.firstName || t('contactsGrid.unnamedContact'),
      token: l.name || l.firstName ? null : (l.id ? String(l.id).slice(0, 8) : null),
      email: l.email, jobTitle: l.jobTitle ?? null, companyName: l.companyName ?? null,
      lastActivityAt: l.lastActivityAt ?? l.lastConversationAt,
    })),
  });
  const contacts = list.data ?? [];

  return (
    <div className="relative w-full">
      <ListStateShell status={list.status} testIdBase="contacts-grid" onRetry={list.retry}>
      {!isDesktop ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 p-2">
          {contacts.map((c) => <ContactCardRow key={c.leadId} contact={c} selected={c.id === selectedId} onOpenPerson={onOpenPerson} />)}
          {contacts.length === 0 && <div className="col-span-full text-center text-sm text-text-muted py-10">{t('contactsGrid.empty')}</div>}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-start border-collapse">
            <thead>
              <tr className="text-label-caps uppercase text-text-muted border-b border-border-neutral">
                <th className="sticky z-10 bg-surface p-cell-y px-cell-x text-start [inset-inline-start:0]">{t('contactsGrid.columns.name')}</th>
                <th className="p-cell-y px-cell-x text-start">{t('contactsGrid.columns.account')}</th>
                <th className="p-cell-y px-cell-x text-start">{t('contactsGrid.columns.title')}</th>
                <th className="p-cell-y px-cell-x text-start">{t('contactsGrid.columns.email')}</th>
                <th className="p-cell-y px-cell-x text-start">{t('contactsGrid.columns.lastActivity')}</th>
              </tr>
            </thead>
            <tbody>
              {contacts.map((c) => <ContactGridRow key={c.leadId} contact={c} selected={c.id === selectedId} onOpenPerson={onOpenPerson} />)}
            </tbody>
          </table>
          {contacts.length === 0 && <div className="text-center text-sm text-text-muted py-10">{t('contactsGrid.empty')}</div>}
        </div>
      )}
      </ListStateShell>
    </div>
  );
};
